import React from 'react';
import { BreadcrumbItemProps } from '../components/Breadcrumbs/Breadcrumbs';
import { ArticleRichText } from '../components/ArticleRichText/ArticleRichText';
import { GenericPageProps } from '../views/GenericPage/GenericPage';
import { DEFAULT_CONTENT_HUB_LABEL, DEFAULT_CONTENT_HUB_URL } from './contentfulApiArticleToArticleProps';
import { ContentfulGenericPage } from '@jbhi-fi/apis/src/contentful/types';
import { ArticleRichTextLayoutBlockContextProvider } from '../hooks/useArticleRichTextLayoutBlockProps';
import { ThemeTransformer } from '@jbhi-fi/components/src/Contentful/transformers/ThemeTransformer';
import { ContentfulImage } from '@jbhi-fi/components/src/ContentfulImage/ContentfulImage';
import { AssetTransformer } from '@jbhi-fi/components/src/Contentful/transformers/AssetTransformer';
import { palette } from '@jbhi-fi/lanyard-ui/themes';
import { MultiColumnBlockTransformer } from '@jbhi-fi/components/src/Contentful/transformers/MultiColumnBlockTransformer';
import { Block, BLOCKS, Inline } from '@contentful/rich-text-types';
import { ContentEntry } from '@jbhi-fi/components/src/ContentEntry/ContentEntry';
import { HeroBanner } from '@jbhi-fi/components/src/HeroBanner/HeroBanner';
import { LeaderboardBannerTransformer } from '@jbhi-fi/components/src/Contentful/transformers/LeaderboardBannerTransformer';
import { ArticleRichTextLayoutBlock } from '../components/ArticleRichText/ArticleRichTextLayoutBlock/ArticleRichTextLayoutBlock';

const getHeroSectionValue = (heroSection?: string) => {
  switch (heroSection) {
    case 'Icon size':
      return 'icon';
    case 'Half size':
      return 'half';
    case 'Full size':
      return 'full';
    case 'Content size':
      return 'content';
    default:
      return undefined;
  }
};
export const renderEmbeddedEntry = (node: Block | Inline): React.ReactNode => {
  return (
    <ContentEntry
      entry={node.data.target}
      Wrapper={ArticleRichTextLayoutBlock}
      overrides={{
        leaderboardBanner: {
          Component: HeroBanner,
          transformer: e =>
            LeaderboardBannerTransformer.toHeroBannerProps(
              e,
              'genericpage-leaderboard-banner',
              'genericPage',
              undefined
            ),
        },
      }}
    />
  );
};

export const contentfulApiGenericPageToGenericPageProps = (
  contentfulApiGenericPage: Partial<ContentfulGenericPage>
): GenericPageProps => {
  const { fields } = contentfulApiGenericPage;
  return {
    content: fields?.mainBody ? (
      <ArticleRichTextLayoutBlockContextProvider
        paddingLeft={{ mobile: 4, desktop: 4, desktopLg: 0 }}
        paddingRight={{ mobile: 4, desktop: 4, desktopLg: 0 }}
      >
        <ArticleRichText
          document={fields.mainBody}
          renderOptions={{
            renderNode: {
              [BLOCKS.EMBEDDED_ENTRY]: renderEmbeddedEntry,
            },
          }}
        />
      </ArticleRichTextLayoutBlockContextProvider>
    ) : null,
    htmlContent: fields?.seoContentB,
    heading: fields?.heading ?? '',
    subheading: fields?.seoContentA,
    handle: fields?.urlSlug ?? '',
    breadcrumbs: {
      colorHex:
        fields?.heroSection && fields?.heroTheme ? fields?.heroTheme?.fields?.textColourHex : palette.primary.neutral,
      items: genericPageToBreadcrumbs(contentfulApiGenericPage),
    },
    heroBackgroundColor:
      ThemeTransformer.toContentThemePalette(contentfulApiGenericPage.fields?.heroTheme).background ??
      palette.secondary.main,
    heroLogo: contentfulApiGenericPage.fields?.heroLogo ? (
      <ContentfulImage
        {...AssetTransformer.toContentfulImageProps(contentfulApiGenericPage.fields?.heroLogo)}
        loading="eager"
        isProgressive={false}
        width="100%"
      />
    ) : undefined,
    heroSection: getHeroSectionValue(fields?.heroSection),
    preFooterSectionProps: fields?.preFooterSection
      ? MultiColumnBlockTransformer.toMultiColumnBlockProps(fields?.preFooterSection)
      : undefined,
  };
};

export const genericPageToBreadcrumbs = (
  contentfulGenericPage: Partial<ContentfulGenericPage>
): Array<BreadcrumbItemProps> => {
  const { fields } = contentfulGenericPage;
  if (fields?.subDirectory === 'Pages') {
    return [
      { href: '/', label: 'Home' },
      {
        href: ``,
        label: fields?.heading,
        isActive: true,
      } as BreadcrumbItemProps,
    ];
  }
  const breadcrumbs = [
    { href: '/', label: 'Home' },
    {
      href: window.contentHubUrl ?? DEFAULT_CONTENT_HUB_URL,
      label: window.contentHubLabel ?? DEFAULT_CONTENT_HUB_LABEL,
    },
  ];

  if (fields?.parentPage?.fields !== undefined) {
    if ('categoryPage' in fields.parentPage.fields) {
      breadcrumbs.push({
        href: `/blogs/${fields?.parentPage?.fields?.categoryPage?.fields?.urlSlug ?? ''}`,
        label: fields?.parentPage?.fields?.categoryPage?.fields?.heading ?? '',
      });
    }
    if (!isContentHubHomePage(contentfulGenericPage)) {
      breadcrumbs.push({
        href: `${'categoryPage' in fields.parentPage.fields ? '' : '../'}${fields?.parentPage?.fields?.urlSlug}`,
        label: fields?.parentPage?.fields?.heading,
      });
    }
  }

  breadcrumbs.push({
    href: '',
    label: fields?.heading,
    isActive: true,
  } as BreadcrumbItemProps);

  return breadcrumbs;
};

export const isContentHubHomePage = ({ fields }: Partial<ContentfulGenericPage>): boolean => {
  if (fields?.parentPage?.fields === undefined) return false;

  return fields.parentPage.fields.urlSlug === DEFAULT_CONTENT_HUB_URL.replace('/blogs/', '');
};
